import React, { useEffect, useMemo, useState } from 'react'
import { AlertCircle, Download, Send, Share } from 'lucide-react'
import { toast } from 'react-toastify'
import { Document, Page, pdfjs } from 'react-pdf'
import {
  useSendAnnualReportMutation,
  useSendDonationReceiptMutation,
} from '../hooks'
import { useExportPdfQuery } from '../hooks/useExportPdfQuery'
import { CURRENT_YEAR } from '../constants/receipt'
import { useDownloadReceiptMutation } from '../hooks/useDownloadReceiptMutation'
import { ConfirmDialog } from './ConfirmDialog'
import { Modal } from '@/components/custom/modal'
import LoadingButton from '@/components/custom/loading-button'
import { useDonationsStore } from '@/features/donations/store/donationsStore'
import { useMembersStore } from '@/features/members/store/membersStore'
import { useIsMobile } from '@/hooks'
import { dateInToronto, formatDate } from '@/utils/timeZoneUtils'
import { Select } from '@/components/custom/select'

// Set up PDF.js worker from public folder (exact version 5.4.296)
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js'

type ExportDialogProps = {
  id: string | null
  trigger?: React.ReactNode
  type: 'donation' | 'annual'
  memberId?: string | null
  defaultYear?: number | null
}

const ExportDialog: React.FC<ExportDialogProps> = ({
  id,
  trigger,
  type,
  memberId,
  defaultYear,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [year, setYear] = useState<number | null>(defaultYear ?? null)
  const [confirmSendOpen, setConfirmSendOpen] = useState(false)
  const [numPages, setNumPages] = useState<number | null>(null)
  const isMobile = useIsMobile()

  const donations = useDonationsStore((s) => s.donations)
  const members = useMembersStore((s) => s.members)

  // Compute available years for annual report based on member's donations
  const availableYears = useMemo(() => {
    if (type !== 'annual' || !memberId) return []

    const memberDonations = donations.filter(
      (d) => d.donorId === memberId && d.date && !d.isRegistrationFee,
    )

    const years = new Set(
      memberDonations.map((d) => new Date(d.date).getFullYear()),
    )

    return Array.from(years)
      .filter((y) => y < CURRENT_YEAR)
      .sort((a, b) => b - a)
  }, [donations, memberId, type])

  const donation = useMemo(
    () => donations.find((d) => d._id === id),
    [donations, id],
  )

  const donor = useMemo(() => {
    if (type === 'annual' && memberId) {
      return members.find((m) => m._id === memberId)
    } else if (donation) {
      return members.find((m) => m._id === donation.donorId)
    }
    return undefined
  }, [members, donation, type, memberId])

  const { mutateAsync: sendReceiptEmailMutation, isPending: isSending } =
    useSendDonationReceiptMutation()
  const { mutateAsync: sendAnnualReportMutation, isPending: isSendingAnnual } =
    useSendAnnualReportMutation()
  const { mutateAsync: downloadReceiptMutation, isPending: isDownloading } =
    useDownloadReceiptMutation()
  const isSendingEmail = isSending || isSendingAnnual

  const {
    data: pdfBlob,
    isLoading,
    refetch,
  } = useExportPdfQuery({
    isOpen,
    type,
    donor,
    donation,
    memberId,
    summaryYear: year,
    enabled:
      type === 'donation'
        ? isOpen && !!donor && !!donation
        : isOpen && !!donor && !!memberId && year !== null,
  })

  // Create and cleanup PDF URL
  const pdfUrl = useMemo(() => {
    if (!pdfBlob) return ''
    return URL.createObjectURL(pdfBlob)
  }, [pdfBlob])

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
    }
  }, [pdfUrl])

  useEffect(() => {
    if (isOpen && defaultYear) {
      setYear(defaultYear)
    }
  }, [isOpen, defaultYear])

  useEffect(() => {
    if (type === 'donation' && isOpen) {
      refetch()
    }
    if (type === 'annual' && isOpen && year !== null) {
      refetch()
    }
  }, [isOpen, year, type, refetch])

  const getTitle = () => {
    const titles: Record<string, string> = {
      donation: 'Donation Receipt',
      annual: 'Donation Summary',
    }
    return titles[type] ?? ''
  }

  const getFilename = () => {
    if (!donor) return 'document.pdf'
    if (type === 'annual') {
      return `${getTitle()} ${donor.name} ${year}.pdf`
    } else if (donation) {
      return `${getTitle()} ${donor.name} ${dateInToronto(donation.date).format('DDMMYYYY')}.pdf`
    }
    return `${getTitle()} ${donor.name}.pdf`
  }

  const handleConfirmSend = async () => {
    if (type === 'donation' && donation) {
      try {
        await downloadReceiptMutation({ donationId: donation._id })
        setConfirmSendOpen(false)
      } catch (error) {
        console.error('Error confirming download:', error)
      }
    }
  }

  const handleDownload = () => {
    if (!pdfBlob || !pdfUrl) return

    try {
      const link = document.createElement('a')
      link.href = pdfUrl
      link.download = getFilename()
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      // Open confirmation dialog instead of marking immediately
      setConfirmSendOpen(true)
    } catch (error) {
      console.error('Error downloading receipt:', error)
      toast.error('Failed to download receipt.')
    }
  }

  const handleShare = async () => {
    if (!pdfBlob) return

    try {
      const file = new File([pdfBlob], getFilename(), { type: pdfBlob.type })

      if (
        typeof navigator.canShare === 'function' &&
        navigator.canShare({ files: [file] })
      ) {
        await navigator.share({
          files: [file],
          title: getTitle(),
          text: `Here is your ${getTitle().toLowerCase()}`,
        })

        // Open confirmation dialog instead of marking immediately
        setConfirmSendOpen(true)
      } else {
        toast.error('Sharing is not supported on this device/browser.')
      }
    } catch (error) {
      // User cancelled the share - don't show error
      if ((error as Error).name === 'AbortError') return

      console.error('Error sharing receipt:', error)
      toast.error('Failed to share receipt. Please try again.')
    }
  }

  const handleSendEmail = async () => {
    if (pdfBlob && donation && donor?.email) {
      try {
        await sendReceiptEmailMutation({
          donationId: donation._id,
          receipt: pdfBlob,
          receiptCode: donation.receiptNumber,
        })
        toast.success(`Receipt sent to ${donor.email}`)
        setIsOpen(false)
      } catch (error) {
        console.error('Error sending donation receipt email:', error)
        toast.error('Failed to send email. Please try again later.')
      }
    }
  }

  const handleSendAnnualReport = async () => {
    if (donor && donor.email) {
      try {
        await sendAnnualReportMutation({
          memberId: donor._id,
          year: year as number,
        })
        toast.success(`Annual summary sent to ${donor.email}`)
        setIsOpen(false)
      } catch (error) {
        console.error('Error sending annual report:', error)
        toast.error('Failed to send annual report. Please try again later.')
      }
    }
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  const onDocumentLoadSuccess = ({ numPages: pages }: { numPages: number }) => {
    setNumPages(pages)
  }

  return (
    <>
      <Modal
        open={isOpen}
        onClose={handleClose}
        title={getTitle()}
        trigger={
          trigger ? (
            <div
              className={isMobile ? 'w-full' : ''}
              onClick={() => setIsOpen(true)}
            >
              {trigger}
            </div>
          ) : undefined
        }
        contentClassName={
          isMobile
            ? 'min-w-[100vw] max-w-[100vw] min-h-60 px-1'
            : '!max-w-fit !w-fit'
        }
      >
        {/* Year dropdown for annual report — hidden when year is pre-selected by the caller */}
        {type === 'annual' && availableYears.length > 0 && !defaultYear && (
          <div className="flex items-center gap-2 mb-4">
            <Select
              label="Report Year"
              field={{
                state: {
                  value: year !== null ? String(year) : '',
                  meta: {
                    isTouched: false,
                    isValid: true,
                    errors: [],
                    isValidating: false,
                  },
                },
                handleChange: (val: string) => setYear(Number(val)),
              }}
              options={availableYears.map((y) => ({
                value: String(y),
                label: String(y),
              }))}
              id="annual-year-select"
              name="annual-year-select"
              size="default"
              required
            />
          </div>
        )}
        {type === 'annual' && availableYears.length === 0 ? (
          <div className="text-center py-10 px-4">
            <AlertCircle className="h-8 w-8 mx-auto mb-2 text-amber-400" />

            <p className="text-gray-600 font-bold">
              No annual reports are available.
            </p>
            <p className="text-sm text-gray-500">
              There are no donations from previous years to generate annual
              summaries.
            </p>
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center w-[95vw] max-w-212.5 h-[65vh]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-700 mx-auto mb-4"></div>
              <p>{`Generating ${getTitle().toLowerCase()}`}</p>
            </div>
          </div>
        ) : type === 'annual' &&
          availableYears.length > 0 &&
          year === null &&
          !defaultYear ? (
          <div className="flex items-center justify-center h-100">
            <p>Please select a year to generate the annual summary.</p>
          </div>
        ) : pdfBlob && pdfUrl ? (
          <>
            {donation?.sentToMember && (
              <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertCircle
                    className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} text-amber-600`}
                  />
                  <span
                    className={`font-medium text-amber-800 ${isMobile ? 'text-sm' : ''}`}
                  >
                    Receipt sent at{' '}
                    {donation.sentToMemberAt &&
                      formatDate(
                        donation.sentToMemberAt,
                        'DD/MM/YYYY [at] h:mm A',
                      )}
                  </span>
                </div>
              </div>
            )}
            <div className="flex flex-col items-center gap-4 p-2 rounded">
              <div
                className={`${isMobile ? `w-[85vw] max-w-fit max-h-[50vh]` : 'w-[95vw] max-w-fit max-h-[65vh]'}  overflow-auto shadow-lg rounded`}
              >
                <Document
                  file={pdfUrl}
                  onLoadSuccess={onDocumentLoadSuccess}
                  loading={
                    <div className="flex items-center justify-center h-100">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-700"></div>
                    </div>
                  }
                  error={
                    <div className="flex items-center justify-center h-100">
                      <p>Error loading PDF. Please try again.</p>
                    </div>
                  }
                >
                  {numPages &&
                    Array.from(new Array(numPages), (_, index) => (
                      <Page
                        key={`page_${index + 1}`}
                        pageNumber={index + 1}
                        width={isMobile ? window.innerWidth - 20 : 850}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                      />
                    ))}
                </Document>
              </div>
            </div>

            <div className="flex items-center justify-end mt-4 gap-2">
              {type === 'donation' && donation && donor?.email && (
                <LoadingButton
                  onClick={handleSendEmail}
                  variant="outline"
                  isLoading={isSendingEmail}
                >
                  <Send
                    className={`mr-2 ${isMobile ? 'h-3 w-3' : 'h-4 w-4'}`}
                  />
                  Send Email
                </LoadingButton>
              )}

              {type === 'annual' && donor && donor.email && (
                <LoadingButton
                  onClick={handleSendAnnualReport}
                  variant="outline"
                  isLoading={isSendingEmail}
                >
                  <Send
                    className={`mr-2 ${isMobile ? 'h-3 w-3' : 'h-4 w-4'}`}
                  />
                  Send Email
                </LoadingButton>
              )}

              {!isMobile && (
                <LoadingButton
                  onClick={handleDownload}
                  variant="outline"
                  isLoading={isDownloading}
                >
                  <Download className={`mr-2 h-4 w-4`} />
                  Download PDF
                </LoadingButton>
              )}
              {isMobile && (
                <LoadingButton
                  onClick={handleShare}
                  variant="outline"
                  isLoading={isDownloading}
                >
                  <Share className={`mr-2 h-3 w-3`} />
                  Share
                </LoadingButton>
              )}
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-100">
            <p>Error generating document. Please try again.</p>
          </div>
        )}
      </Modal>

      {/* Confirmation dialog for send */}
      <ConfirmDialog
        open={confirmSendOpen}
        title="Mark Receipt as Sent"
        description={`Did you successfully send the ${getTitle().toLowerCase()} to ${donor?.name}?`}
        confirmText="Yes, Mark as Sent"
        cancelText="Cancel"
        onConfirm={handleConfirmSend}
        onCancel={() => setConfirmSendOpen(false)}
        isLoading={isDownloading}
        variant="default"
      />
    </>
  )
}

export default ExportDialog
